/**
 * Accesos → Personas, the list (Plan J, J3 — mockup 5, list half).
 *
 * Replaces `UsersAdminPage`'s `UsersPane`. What survives: the role
 * filter, the total count, the create button, the pagination, the
 * builder-access badge, the reset-password and delete row actions. What
 * changes: the table is the shared `DataTable`, and the two columns that
 * mattered under per-user permissions are replaced by the two that
 * matter under groups.
 *
 * WHAT WAS DROPPED, AND WHY THAT IS SAFE
 * --------------------------------------
 * * **"Clonar desde otra persona"** (`CloneFromAnother` /
 *   `adminPermissionsApi.cloneFrom`). With groups there is nothing
 *   per-user left to clone — the rows it copied are the rows being
 *   retired. The replacement, "add them to the same groups", is
 *   strictly better by Pablo's own argument for one-person groups
 *   (2026-09-07): a group survives the person, so "same access as Ana"
 *   keeps working after Ana leaves, while a cloned row set is a snapshot
 *   that silently stops matching the role it was copied from. The
 *   endpoint stays on the backend until the legacy tables are dropped.
 * * **The per-user permission matrix link.** It is the thing being
 *   replaced; the group's Contenido tab is where a cell is edited now.
 * * **`subscription_count` / `override_count` as columns.** They count
 *   rows in the tables this slice retires. They are still on the
 *   payload, and the person detail shows them as a legacy note, but
 *   putting them in the list would teach an operator to read the number
 *   that is about to stop meaning anything.
 *
 * WHERE THIS LIST COMES FROM
 * --------------------------
 * `listPeople` in `access-api.ts`, which JOINS `/admin/users` with the
 * groups router IN THE BROWSER because there is no
 * `GET /api/builder/admin/people`. Its own docstring carries the cost
 * and the argument for moving it server-side. A group whose membership
 * could not be read is reported in the footnote rather than quietly
 * lowering somebody's group count.
 */

import {
  Badge,
  Button,
  Pagination,
  Select,
  type SelectOption,
} from "@omnitok/ui";
import {
  Callout,
  DataTable,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  StatusChip,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Eye, KeyRound, Mail, Plus, Send, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useT } from "@/i18n";
import {
  EFFECTIVE_SCOPE_ID_PREFIX,
  SCOPE_ALL,
  listPeople,
  type PeopleListOut,
  type PersonRow,
} from "@/lib/access-api";
import { formatDateTime, formatRelativeTime } from "@/lib/admin-ai-api";
import { ADMIN_USER_ROLES, adminUsersApi } from "@/lib/admin-users-api";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import {
  CreatePersonWizard,
  type CreatedPerson,
} from "@/pages/admin-access/CreatePersonWizard";
import { DeletePersonModal } from "@/pages/admin-access/DeletePersonModal";
import { ResetPasswordModal } from "@/pages/admin-access/ResetPasswordModal";

const PAGE_SIZE = 50;

export function AccessPeoplePage(): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [data, setData] = useState<PeopleListOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [role, setRole] = useState("");
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<PersonRow | null>(null);
  const [deleting, setDeleting] = useState<PersonRow | null>(null);
  // What the last account mail did: shown above the table until the
  // next one. A failure is never folded into a success message.
  const [mailNotice, setMailNotice] = useState<
    | { tone: "info"; text: string }
    | { tone: "danger"; text: string }
    | null
  >(null);

  const reportMail = useCallback(
    (created: CreatedPerson): void => {
      if (created.mail.status === "sent") {
        setMailNotice({
          tone: "info",
          text: t("access.people.mail.inviteSent", { email: created.email }),
        });
      } else if (created.mail.status === "failed") {
        setMailNotice({
          tone: "danger",
          text: t("access.people.mail.inviteFailed", {
            email: created.email,
            error: created.mail.error ?? "",
          }),
        });
      }
    },
    [t],
  );

  const mailAction = useCallback(
    async (row: PersonRow, kind: "invitation" | "reset"): Promise<void> => {
      setMailNotice(null);
      try {
        if (kind === "invitation") await adminUsersApi.resendInvitation(row.user.id);
        else await adminUsersApi.sendResetLink(row.user.id);
        setMailNotice({
          tone: "info",
          text: t(
            kind === "invitation"
              ? "access.people.mail.inviteSent"
              : "access.people.mail.resetSent",
            { email: row.user.email },
          ),
        });
      } catch (err) {
        setMailNotice({
          tone: "danger",
          text: t("access.people.mail.actionFailed", {
            email: row.user.email,
            error: err instanceof Error ? err.message : String(err),
          }),
        });
      }
    },
    [t],
  );

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await listPeople({
          role: role === "" ? undefined : role,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [role, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const columns: Column<PersonRow>[] = [
    {
      id: "person",
      header: t("access.people.col.person"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">
            {row.user.name}
          </div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.user.email}
          </div>
        </div>
      ),
    },
    {
      id: "role",
      header: t("access.people.col.role"),
      width: "w-28",
      cell: (row) => (
        <Badge
          variant={row.user.role === "admin" ? "error" : "neutral"}
          size="sm"
          pill
        >
          {row.user.role}
        </Badge>
      ),
    },
    {
      id: "groups",
      header: t("access.people.col.groups"),
      width: "w-56",
      cell: (row) => <GroupsCell row={row} t={t} />,
    },
    {
      id: "effective",
      header: t("access.people.col.effective"),
      width: "w-52",
      cell: (row) => <EffectiveCell row={row} t={t} />,
    },
    {
      id: "builder",
      header: t("access.people.col.builder"),
      width: "w-36",
      cell: (row) =>
        row.user.builder_access === "none" ? (
          <span className="text-[13px] text-neutral-500">
            {t("access.people.builderNone")}
          </span>
        ) : (
          <Badge
            variant={row.user.builder_access === "full" ? "success" : "info"}
            size="sm"
            pill
          >
            {row.user.builder_access === "full"
              ? t("access.people.builderFull")
              : t("access.people.builderPartial")}
          </Badge>
        ),
    },
    {
      id: "lastLogin",
      header: t("access.people.col.lastLogin"),
      width: "w-44",
      cell: (row) => (
        <LastLoginCell
          row={row}
          recordStartsAt={data?.loginsRecordStartsAt ?? null}
          t={t}
          locale={locale}
        />
      ),
    },
    {
      id: "status",
      header: t("access.people.col.status"),
      width: "w-32",
      cell: (row) => (
        <StatusChip
          label={
            row.user.is_active
              ? t("access.people.active")
              : t("access.people.inactive")
          }
          tone={row.user.is_active ? "success" : "neutral"}
        />
      ),
    },
  ];

  const actions: RowAction<PersonRow>[] = [
    {
      id: "detail",
      label: t("access.people.actions.detail"),
      icon: <Eye size={14} />,
      onSelect: (row) =>
        navigate(`/admin/access/people/${encodeURIComponent(row.user.id)}`),
    },
    {
      id: "invite",
      label: t("access.people.actions.resendInvite"),
      icon: <Send size={14} />,
      onSelect: (row) => void mailAction(row, "invitation"),
    },
    {
      id: "reset-link",
      label: t("access.people.actions.resetLink"),
      icon: <Mail size={14} />,
      onSelect: (row) => void mailAction(row, "reset"),
    },
    {
      id: "reset",
      label: t("access.people.actions.reset"),
      icon: <KeyRound size={14} />,
      onSelect: (row) => setResetting(row),
    },
    {
      id: "delete",
      label: t("access.people.actions.delete"),
      icon: <Trash2 size={14} />,
      danger: true,
      onSelect: (row) => setDeleting(row),
    },
  ];

  const roleOptions: SelectOption[] = [
    { value: "", label: t("access.people.allRoles") },
    ...ADMIN_USER_ROLES.map((r) => ({ value: r, label: r })),
  ];

  const totalPages =
    data === null ? 1 : Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <PageContainer wide>
      <PageHeader
        title={t("access.people.title")}
        subtitle={t("access.people.subtitle")}
        actions={
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setCreating(true)}
          >
            {t("access.people.add")}
          </Button>
        }
      />
      <AccessTabs active="people" />

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      {mailNotice !== null && (
        <Callout
          tone={mailNotice.tone}
          title={
            mailNotice.tone === "info"
              ? t("access.people.mail.okTitle")
              : t("access.people.mail.failTitle")
          }
        >
          <span className="whitespace-pre-wrap">{mailNotice.text}</span>
        </Callout>
      )}

      {data !== null && data.unreadableGroups.length > 0 && (
        <Callout tone="warning" title={t("access.people.partialTitle")}>
          {t("access.people.partial", {
            groups: data.unreadableGroups.map((g) => g.group_name).join(", "),
          })}
        </Callout>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Select
          aria-label={t("access.people.col.role")}
          options={roleOptions}
          value={role}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setRole(e.target.value);
            setPage(0);
          }}
        />
        {/* Only once there IS a total. `?? 0` here would print "0
            personas" for the length of every load — a number that means
            "not asked" wearing the clothes of an answer. */}
        {data !== null && (
          <span className="text-sm text-neutral-600">
            {t("access.people.count", { count: data.total })}
          </span>
        )}
      </div>

      <Section flush>
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        ) : data === null ? null : (
          <DataTable
            columns={columns}
            rows={data.people}
            rowKey={(row) => row.user.id}
            actions={actions}
            caption={t("access.people.title")}
            onRowClick={(row) =>
              navigate(`/admin/access/people/${encodeURIComponent(row.user.id)}`)
            }
            empty={
              <EmptyState
                icon={<Users size={24} />}
                title={t("access.people.empty.title")}
                description={t("access.people.empty.desc")}
              />
            }
          />
        )}
      </Section>

      {totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            currentPage={page + 1}
            totalPages={totalPages}
            totalItems={data?.total ?? 0}
            itemsPerPage={PAGE_SIZE}
            onChange={(next: number) => setPage(next - 1)}
          />
        </div>
      )}

      <CreatePersonWizard
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          if (created !== null) reportMail(created);
          void refresh();
        }}
      />

      <ResetPasswordModal
        user={resetting?.user ?? null}
        onClose={() => setResetting(null)}
      />

      <DeletePersonModal
        user={deleting?.user ?? null}
        groups={deleting?.groups.length ?? 0}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void refresh();
        }}
        remove={(id) => adminUsersApi.remove(id)}
      />
    </PageContainer>
  );
}

/**
 * The person's last portal login and how many in the last 30 days.
 *
 * No login is said against the start of the record ("no logins since
 * <date>"), never as "never": before the first row of `user_logins`
 * (portal migration 079) nothing was recorded.
 */
export function LastLoginCell({
  row,
  recordStartsAt,
  t,
  locale,
}: {
  row: PersonRow;
  recordStartsAt: string | null;
  t: (key: string, params?: Record<string, string | number>) => string;
  locale: string;
}): JSX.Element {
  const last = row.user.last_login_at;
  if (last === null) {
    return (
      <span className="text-[13px] text-neutral-500">
        {recordStartsAt === null
          ? t("access.people.lastLogin.noneYet")
          : t("access.people.lastLogin.noneSince", {
              date: formatDateTime(recordStartsAt),
            })}
      </span>
    );
  }
  return (
    <div className="min-w-0" title={formatDateTime(last)}>
      <div className="truncate text-[13px] text-neutral-900">
        {formatRelativeTime(last, locale)}
      </div>
      <div className="truncate text-[12px] text-neutral-500">
        {t("access.people.lastLogin.window", { n: row.user.logins_30d })}
      </div>
    </div>
  );
}

/**
 * "n grupos" — and NO GROUPS is a warning, not an em-dash.
 *
 * A person in no group is granted no report by any group. That is the
 * single most consequential fact this list can carry about somebody, and
 * a neutral dash reads as "nothing to see here".
 */
export function GroupsCell({
  row,
  t,
}: {
  row: PersonRow;
  t: (key: string, params?: Record<string, string | number>) => string;
}): JSX.Element {
  if (row.groups.length === 0) {
    return (
      <StatusChip
        label={t("access.people.noGroups")}
        tone="warning"
        title={t("access.people.noGroupsHint")}
      />
    );
  }
  return (
    <span
      className="text-[13px] text-neutral-700"
      title={row.groups.map((g) => g.name).join(", ")}
    >
      {t("access.people.groupCount", { n: row.groups.length })}
    </span>
  );
}

/**
 * The effective scope, as a NAME — never the `eff_…` id.
 *
 * The id is a sha256 prefix derived from the normalised rules; it names
 * nothing to a human. When no group reports one, the cell says UNKNOWN
 * rather than assuming the column's `'all'` default: the builder exposes
 * no endpoint that carries `users.effective_scope_id` for a person
 * outside every group.
 */
export function EffectiveCell({
  row,
  t,
}: {
  row: PersonRow;
  t: (key: string, params?: Record<string, string | number>) => string;
}): JSX.Element {
  if (row.effective_scope_id === null) {
    return (
      <StatusChip
        label={t("access.people.effective.unknown")}
        tone="warning"
        title={t("access.people.effective.unknownHint")}
      />
    );
  }
  if (row.effective_scope_id === SCOPE_ALL) {
    return (
      <StatusChip
        label={t("access.scopes.all")}
        tone="warning"
        title={t("access.people.effective.allHint")}
      />
    );
  }
  if (row.effective_scope_id.startsWith(EFFECTIVE_SCOPE_ID_PREFIX)) {
    return (
      <StatusChip
        label={t("access.people.effective.name", { n: row.groups.length })}
        tone="info"
        title={row.effective_scope_id}
      />
    );
  }
  const named = row.groups.find((g) => g.scope_id === row.effective_scope_id);
  if (named === undefined) {
    // The person's binding is an explicit scope that none of their
    // groups carries — a real and interesting state (the recompute has
    // not caught up, or somebody wrote the column by hand). The raw id
    // is shown as a defect, the way `resolveStatus` shows an unlisted
    // status: a plausible name here would hide the disagreement.
    return (
      <StatusChip
        label={row.effective_scope_id}
        tone="warning"
        unknown
        title={t("access.people.effective.orphanHint")}
      />
    );
  }
  return (
    <StatusChip
      label={named.scope_name}
      tone="info"
      title={row.effective_scope_id}
    />
  );
}
